import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';

import { Icon } from '@/core/components/Icons/Icon';
import { CardList } from '@/core/components/CardList';
import { ReportsListSkeleton } from '@/core/components/ReportsListSkeleton';
import { ReportReadSwitchStatus } from '@/core/interfaces/common';
import { useAppDispatch } from '@/core/store/store';
import { DownloadCSVFile } from '@/core/components/DownloadCSVFile/DownloadCSVFile';
import { ReadStatusSwitch } from '@/core/components/ReadStatusSwitch';

import {
  changeReportsReadSwitchStatus,
  getReportsListSelector,
  getReportsReadSwitchStatusSelector,
  isReportsListLoadingSelector,
} from '@/features/Reports/store';
import { isAdvancedFeaturesEnabledSelector } from '@/features/Auth/store';

import {
  PanelWrapper,
  StyledButton,
  ContentWrapper,
  DownloadButtonContainer,
  HeaderContainer,
  NoDataMessage,
} from './CollapsiblePanel.styles';
import { CollapsiblePanelProps } from './CollapsiblePanel.types';
import { Paragraph } from '../Typography';

export const CollapsiblePanel = ({ isOpen, setIsOpen }: CollapsiblePanelProps) => {
  const dispatch = useAppDispatch();
  const activeReadStatus = useSelector(getReportsReadSwitchStatusSelector);
  const reports = useSelector(getReportsListSelector);
  const isReportsLoading = useSelector(isReportsListLoadingSelector);
  const isAdvancedFeaturesEnabled = useSelector(isAdvancedFeaturesEnabledSelector);
  const { colors } = useTheme();

  const handleReadStatusChange = useCallback(
    (status: ReportReadSwitchStatus) => {
      dispatch(changeReportsReadSwitchStatus(status));
    },
    [dispatch]
  );

  return (
    <PanelWrapper isOpen={isOpen}>
      <StyledButton onClick={() => setIsOpen(!isOpen)}>
        <Icon
          name={isOpen ? 'ChevronLeft' : 'ChevronRight'}
          color={colors.primaryText}
          size={10}
        />
      </StyledButton>
      <HeaderContainer>
        <ReadStatusSwitch
          activeStatus={activeReadStatus}
          onStatusChange={handleReadStatusChange}
        />
        {isAdvancedFeaturesEnabled && (
          <DownloadButtonContainer>
            <DownloadCSVFile reports={reports} />
          </DownloadButtonContainer>
        )}
      </HeaderContainer>
      <ContentWrapper>
        {isReportsLoading ? (
          <ReportsListSkeleton />
        ) : reports.length === 0 ? (
          <NoDataMessage>
            <Paragraph
              type="normal"
              color={colors.secondaryText}
            >
              No data
            </Paragraph>
          </NoDataMessage>
        ) : (
          <CardList reports={reports} />
        )}
      </ContentWrapper>
    </PanelWrapper>
  );
};
